import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  X, ArrowRight, ArrowLeft, Sparkle, Microphone,
  Calculator, House, Check, Stack, FolderOpen,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * OnboardingMagico — il "wow" dei primi 60 secondi.
 *
 * Mostrato 1 sola volta a un agente fresh (created_at < 14gg) che non ha
 * ancora visto il tour. Storage flag: localStorage["pronto_onboarding_magico"].
 *
 * 5 slide editoriali (iter 285: ricalibrate sul flusso reale BETA):
 *   1. Hello — claim + intro + bullet 4 vantaggi concreti
 *   2. Foto — editor before/after
 *   3. Voce — agenda vocale dal sopralluogo
 *   4. Stima — OMI in 30s
 *   5. Social Studio — Brochure A4 + carosello Instagram con stesso stile
 *
 * Restartabile via Profile > "Rivedi il tour iniziale" (resetta tutti i flag).
 */
const STORAGE_KEY = "pronto_onboarding_magico";

const SLIDES = [
  {
    id: "hello",
    eyebrow: "Benvenuto in Pronto",
    title: (
      <>
        Le case <span className="font-editorial italic">le vendi tu.</span>
        <br />
        Al resto, ci pensa Pronto.
      </>
    ),
    body: "In 60 secondi ti mostro le 5 cose più potenti dell'app. Poi sei autonomo.",
    bullets: [
      "Foto migliorate in pochi secondi senza tornare in casa",
      "Agenda vocale: parli, registra, organizza",
      "Stima di mercato in 30 secondi",
      "Una landing tua per raccogliere clienti che vendono casa",
      "PDF stampabile e post Instagram coordinati con 1 click",
    ],
    visual: "hello",
  },
  {
    id: "foto",
    eyebrow: "1 di 5 · Foto migliorate",
    title: (
      <>
        Foto da agente Pro.
        <br />
        <span className="font-editorial italic">In pochi secondi.</span>
      </>
    ),
    body: "Carichi le foto del sopralluogo, premi il pulsante e Pronto le ottimizza: luce, prospettiva, dettagli. Senza tornare in casa, senza fotografo. Tu confermi quelle che funzionano e le metti subito online.",
    visual: "foto",
  },
  {
    id: "voce",
    eyebrow: "2 di 5 · Agenda vocale",
    title: (
      <>
        <span className="font-editorial italic">Detto.</span> Fatto.
      </>
    ),
    body: "Esci dal sopralluogo, premi il microfono e parli normalmente. Pronto trascrive, organizza i dati dell'immobile e crea i promemoria. Niente blocchi note sparsi.",
    visual: "voce",
  },
  {
    id: "stima",
    eyebrow: "3 di 5 · Stima di mercato",
    title: (
      <>
        Quanto vale?
        <br />
        <span className="font-editorial italic">In 30 secondi.</span>
      </>
    ),
    body: "Indirizzo, metratura, stato. Ti diamo un range basato sui dati ufficiali OMI dell'Agenzia delle Entrate, più trend ISTAT degli ultimi trimestri. Comoda anche da inviare al cliente come PDF di stima comparativa.",
    visual: "stima",
  },
  {
    id: "landing",
    eyebrow: "4 di 5 · Pagina stima del cliente",
    title: (
      <>
        Una landing tua.
        <br />
        <span className="font-editorial italic">Per raccogliere clienti.</span>
      </>
    ),
    body: "Ogni agente ha una pagina pubblica (es. /p/tuo-nome) dove i proprietari possono chiedere una stima della loro casa. I lead arrivano nel tuo CRM con città, metratura, motivo della vendita e timing. La trovi nel menu \"Pagina stima del cliente\".",
    visual: "landing",
  },
  {
    id: "social",
    eyebrow: "5 di 5 · Editor social",
    title: (
      <>
        PDF stampabile e Instagram.
        <br />
        <span className="font-editorial italic">Stesso stile, 1 click.</span>
      </>
    ),
    body: "Scegli un layout (Elegante / Luxury / Moderno / Minimal) e 1 palette. PDF stampabile A4, post quadrato, story 9:16 e reel video escono coordinati col tuo brand. Trovi tutto nella cartella Download con nome \"pronto-{nome-immobile}-…\".",
    visual: "social",
  },
];

export default function OnboardingMagico({ user }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [demoPropId, setDemoPropId] = useState(null);
  // iter v28 r421 — User M1331: mini-banner "Bravo, profilo completato"
  // sulla slide 0 quando il tour arriva dopo aver finito il wizard.
  // Inizializziamo da localStorage perché Home fa window.location.reload()
  // dopo onComplete del wizard (per ricaricare i dati profilo aggiornati),
  // quindi lo state React viene perso. Il flag persistente sopravvive al
  // reload e poi viene rimosso al primo render del banner.
  const [cameFromWizard, setCameFromWizard] = useState(() => {
    try { return localStorage.getItem("pronto:came-from-wizard") === "1"; }
    catch { return false; }
  });
  const location = useLocation();

  // Decide if we should auto-open
  // iter 309 r2: dipende anche da location.search così se l'utente clicca
  // "Rivedi il tour iniziale" da Profilo e nav() porta a `/dashboard?onboarding=1`
  // mentre Home era già montato, l'effect ri-parte e riapre il tour.
  // iter v28 r419: ascolta anche `pronto:start-tour-after-wizard` (emesso da
  // ProfileSetupWizard alla fine), così il tour parte SUBITO dopo che
  // l'agente ha completato il profilo.
  useEffect(() => {
    const handleTriggerEvent = () => {
      setStep(0);
      setCameFromWizard(true);
      setOpen(true);
    };
    window.addEventListener("pronto:start-tour-after-wizard", handleTriggerEvent);
    return () => window.removeEventListener("pronto:start-tour-after-wizard", handleTriggerEvent);
  }, []);

  // iter v28 r421 — Rimuovo il flag "came-from-wizard" SOLO quando l'utente
  // ha visto il banner e ha avanzato dalla slide 0, o quando chiude il tour.
  // Non al mount: in dev React StrictMode rimonta i componenti, e se rimuovo
  // al mount il secondo mount trova il flag vuoto e perde il banner.
  useEffect(() => {
    if (step > 0 && cameFromWizard) {
      try { localStorage.removeItem("pronto:came-from-wizard"); } catch { /* ignore */ }
    }
  }, [step, cameFromWizard]);

  useEffect(() => {
    if (!user) return;
    let urlForce = false;
    try {
      const params = new URLSearchParams(location.search || window.location.search);
      urlForce = params.get("onboarding") === "1" || params.get("tour") === "1";
    } catch (_) { /* SSR-safe */ }

    if (urlForce) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      setStep(0);
      setOpen(true);
      // Pulisce il query param così non si riapre ad ogni navigate interno
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("onboarding");
        url.searchParams.delete("tour");
        window.history.replaceState({}, "", url.toString());
      } catch (_) { /* noop */ }
      return;
    }

    // iter v29 r087 — Voice-first onboarding ha priorità.
    // Se siamo in /dashboard/welcome (voice intake), oppure se l'utente
    // ha appena visto la welcome (`from=welcome` nell'URL), NON
    // aprire il tour Magico — c'è già abbastanza in scena.
    try {
      if (location.pathname === "/dashboard/welcome") return;
      const params = new URLSearchParams(location.search || window.location.search);
      if (params.get("from") === "welcome") return;
    } catch (_) { /* noop */ }

    // Already seen?
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") return;
    } catch (_) { return; }

    // Fresh account only (created < 14d ago)
    if (!user.created_at) return;
    const ageHours = (new Date() - new Date(user.created_at)) / (1000 * 60 * 60);
    if (ageHours > 24 * 14) return;

    // iter v28 r419 — Skip per utenti già operativi.
    // Se ha già almeno 1 annuncio o 1 contatto, il tour è inutile (e fastidioso).
    // Lo lasciamo riapribile via "Rivedi il tour iniziale" da Profilo.
    (async () => {
      try {
        const [propsRes, contactsRes] = await Promise.all([
          api.get("/properties").catch(() => ({ data: [] })),
          api.get("/contacts").catch(() => ({ data: [] })),
        ]);
        const realProps = (propsRes.data || []).filter((p) => !p.is_demo);
        const realContacts = contactsRes.data || [];
        if (realProps.length > 0 || realContacts.length > 0) {
          // Utente già operativo — salva flag e non aprire mai più auto.
          try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_) { /* noop */ }
          return;
        }
        // Slight delay so the dashboard render lands first.
        // Nota: se il ProfileSetupWizard è aperto, il tour aspetta
        // l'evento `pronto:start-tour-after-wizard` (gestito sopra).
        // Qui partiamo solo se NESSUN wizard è aperto.
        setTimeout(() => {
          if (!document.querySelector('[data-testid="profile-wizard"]')) {
            setOpen(true);
          }
        }, 1500);
      } catch (_) { /* noop */ }
    })();
  }, [user, location.search]);

  // Fetch demo property id (used by final CTA)
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/properties");
        const demo = (data || []).find((p) => p.is_demo);
        if (demo) setDemoPropId(demo.id);
      } catch (_) { /* noop */ }
    })();
  }, [open]);

  const close = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_) { /* noop */ }
    // iter v28 r421 — Pulisco anche il flag "came-from-wizard" alla chiusura
    // del tour così non riappare al prossimo "Rivedi tour iniziale".
    try { localStorage.removeItem("pronto:came-from-wizard"); } catch (_) { /* noop */ }
    setOpen(false);
    setStep(0);
  };

  const next = () => {
    if (step < SLIDES.length - 1) setStep(step + 1);
    else close();
  };
  const prev = () => { if (step > 0) setStep(step - 1); };

  const slide = SLIDES[step];
  const isLast = step === SLIDES.length - 1;

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] bg-[#1C1C1A]/72 backdrop-blur-md flex items-end sm:items-center justify-center px-3 sm:px-6 py-4 sm:py-8 animate-[fadeIn_.18s_ease-out]"
      data-testid="onboarding-magico"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-[#FBFAF6] w-full max-w-[640px] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[94vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top bar */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#EAE7DF] bg-white">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#F1F4ED] flex items-center justify-center">
              <Sparkle size={13} weight="fill" className="text-[#7C8B6E]" />
            </div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#7C8B6E] font-bold">
              Tour iniziale · {step + 1}/{SLIDES.length}
            </div>
          </div>
          <button
            onClick={close}
            className="w-8 h-8 rounded-lg hover:bg-[#F1F4ED] flex items-center justify-center text-[#6B6A65]"
            data-testid="onboarding-close"
            aria-label="Chiudi tour"
            title="Chiudi (puoi rivederlo dal Profilo)"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="px-5 pt-4 flex items-center gap-1.5" aria-hidden="true">
          {SLIDES.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-all ${
                i === step ? "bg-[#7C8B6E]" : i < step ? "bg-[#7C8B6E]/40" : "bg-[#EAE7DF]"
              }`}
            />
          ))}
        </div>

        {/* Slide body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-8 py-6">
          {/* iter v28 r421 — Banner congratulazioni: appare SOLO sulla slide 0
              quando l'utente è arrivato qui completando il wizard. Sparisce
              quando si avanza. */}
          {step === 0 && cameFromWizard && (
            <div
              className="mb-5 px-4 py-3 rounded-xl bg-[#F1F4ED] border border-[#D4DCC8] flex items-start gap-3"
              data-testid="onboarding-wizard-bravo"
            >
              <div className="shrink-0 w-9 h-9 rounded-full bg-[#7C8B6E] text-white flex items-center justify-center">
                <Check size={16} weight="bold" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] uppercase tracking-[0.18em] font-bold text-[#5A6A4D]">
                  Bravo · profilo completato
                </div>
                <p className="text-[13px] text-[#3F4A33] leading-snug mt-0.5">
                  La tua pagina pubblica è già attiva. Ora ti spiego l&apos;app in 60 secondi · poi sei autonomo.
                </p>
              </div>
            </div>
          )}
          <SlideVisual kind={slide.visual} />
          <div className="mt-5">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#9A968B] font-bold mb-2.5">
              {slide.eyebrow}
            </div>
            <h2 className="text-[26px] sm:text-[30px] leading-[1.12] tracking-tight text-[#1C1C1A] font-semibold">
              {slide.title}
            </h2>
            <p className="mt-3 text-[14.5px] leading-relaxed text-[#3A3A36]">
              {slide.body}
            </p>
            {slide.bullets && (
              <ul className="mt-4 space-y-2">
                {slide.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13.5px] text-[#3A3A36]">
                    <Check size={14} weight="bold" className="text-[#7C8B6E] mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Footer / nav */}
        <div className="px-5 sm:px-6 py-4 border-t border-[#EAE7DF] bg-white flex items-center justify-between gap-2">
          <button
            onClick={step === 0 ? close : prev}
            className="text-[13px] text-[#6B6A65] hover:text-[#1C1C1A] inline-flex items-center gap-1 px-2 py-1.5"
            data-testid="onboarding-back"
          >
            {step === 0 ? "Salta" : (<><ArrowLeft size={12} weight="bold" /> Indietro</>)}
          </button>

          {isLast ? (
            <div className="flex items-center gap-2">
              {demoPropId && (
                <Link
                  to={`/dashboard/property/${demoPropId}`}
                  onClick={close}
                  data-testid="onboarding-explore-demo"
                  className="text-[13px] font-medium px-3 py-2 rounded-lg border border-[#1C1C1A] text-[#1C1C1A] hover:bg-[#F1F4ED] inline-flex items-center gap-1.5"
                >
                  <House size={13} weight="duotone" /> Esplora demo
                </Link>
              )}
              <Link
                to="/dashboard/property/new"
                onClick={close}
                data-testid="onboarding-create-first"
                className="text-[13px] font-semibold px-3.5 py-2 rounded-lg bg-[#1C1C1A] hover:bg-black text-white inline-flex items-center gap-1.5"
              >
                Crea primo annuncio <ArrowRight size={12} weight="bold" />
              </Link>
            </div>
          ) : (
            <button
              onClick={next}
              data-testid="onboarding-next"
              className="text-[13px] font-semibold px-3.5 py-2 rounded-lg bg-[#1C1C1A] hover:bg-black text-white inline-flex items-center gap-1.5"
            >
              Avanti <ArrowRight size={12} weight="bold" />
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
      `}</style>
    </div>
  );
}

/* ---------- Visual per slide ----------
   Riusiamo asset landing già presenti (camera-before/camera-after) per il
   before/after live. Niente librerie extra: slider CSS-only con clip-path. */
function SlideVisual({ kind }) {
  if (kind === "hello") {
    return (
      <div className="relative h-44 sm:h-52 rounded-xl overflow-hidden bg-gradient-to-br from-[#F1F4ED] to-[#EAE7DF] border border-[#EAE7DF] flex items-center justify-center">
        {/* iter v28 r420 — User M1330: logo ufficiale Pronto (P-casa + tagline)
            invece del testo CSS "Pronto" + "PER AGENTI IMMOBILIARI". */}
        <img
          src="/logo/pronto-official-512.png"
          alt="Pronto · per agenti immobiliari"
          className="h-32 sm:h-40 w-auto select-none drop-shadow-[0_4px_12px_rgba(124,139,110,0.25)]"
          draggable="false"
        />
      </div>
    );
  }
  if (kind === "foto") return <BeforeAfter />;
  if (kind === "voce") return <VoiceMock />;
  if (kind === "stima") return <StimaMock />;
  if (kind === "landing") return <LandingMock />;
  if (kind === "social") return <SocialStudioMock />;
  return null;
}

/** Visual slide "landing" — mockup della pagina pubblica /p/:slug con form
 *  di valutazione e indicatore lead in arrivo. */
function LandingMock() {
  return (
    <div className="relative h-44 sm:h-52 rounded-xl overflow-hidden bg-gradient-to-br from-[#FAFAF7] to-[#EFEDE6] flex items-center justify-center px-6">
      {/* Browser frame */}
      <div className="w-full max-w-[260px] bg-white rounded-md shadow-lg border border-[#E5E2DA] overflow-hidden">
        <div className="h-4 bg-[#F4F1E8] border-b border-[#E5E2DA] flex items-center gap-1 px-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-[#E2BFA1]"></span>
          <span className="w-1.5 h-1.5 rounded-full bg-[#E8D9A8]"></span>
          <span className="w-1.5 h-1.5 rounded-full bg-[#C8D8B8]"></span>
          <div className="ml-2 flex-1 h-2 rounded-sm bg-white/90 text-[6px] tracking-[0.1em] text-[#9A968B] font-mono flex items-center px-1.5">
            prontoimmo.it/p/tuo-nome
          </div>
        </div>
        <div className="px-3 py-3">
          <div className="text-[9px] uppercase tracking-[0.18em] text-[#7C8B6E] font-bold mb-1">Stima gratuita</div>
          <div className="text-[12px] font-semibold text-[#1C1C1A] leading-tight mb-2">Quanto vale<br/>la tua casa?</div>
          <div className="space-y-1">
            <div className="h-3 rounded-sm bg-[#F1F4ED] border border-[#E5E2DA]"></div>
            <div className="h-3 rounded-sm bg-[#F1F4ED] border border-[#E5E2DA]"></div>
          </div>
          <div className="mt-1.5 h-4 rounded-sm bg-[#7C8B6E] text-white text-[7px] uppercase tracking-[0.16em] font-bold flex items-center justify-center">
            Richiedi stima
          </div>
        </div>
      </div>
      <div className="absolute bottom-2 left-0 right-0 text-center text-[9px] uppercase tracking-[0.22em] text-[#7C8B6E] font-semibold">
        Lead diretti al tuo CRM
      </div>
    </div>
  );
}

/** Visual slide 5: 4 mini-mockup formati (Post 1:1 / Story 9:16 / Brochure
 *  Social 4:5 / Brochure A4) tutti con stessa palette → mostra coerenza. */
function SocialStudioMock() {
  return (
    <div className="relative h-44 sm:h-52 rounded-xl overflow-hidden bg-gradient-to-br from-[#FAFAF7] to-[#EFEDE6] flex items-center justify-center px-4">
      <div className="flex items-end gap-2.5">
        {/* Post 1:1 */}
        <div className="w-20 h-20 rounded-md bg-gradient-to-br from-[#7C8B6E] to-[#5A6B4D] shadow-lg flex flex-col justify-end p-2">
          <span className="text-[8px] uppercase font-bold text-white/95 tracking-wider leading-none">Post</span>
          <span className="text-[7px] text-white/70 leading-none mt-0.5">1:1 · feed</span>
        </div>
        {/* Story 9:16 */}
        <div className="w-12 h-24 rounded-md bg-gradient-to-br from-[#C8A66B] to-[#A88848] shadow-lg flex flex-col justify-end p-2">
          <span className="text-[8px] uppercase font-bold text-white/95 tracking-wider leading-none">Story</span>
          <span className="text-[7px] text-white/70 leading-none mt-0.5">9:16</span>
        </div>
        {/* Brochure social 4:5 */}
        <div className="w-[60px] h-[88px] rounded-md bg-gradient-to-br from-[#7E9CAE] to-[#5C7A8C] shadow-lg flex flex-col justify-end p-2">
          <span className="text-[8px] uppercase font-bold text-white/95 tracking-wider leading-none">PDF stampabile</span>
          <span className="text-[7px] text-white/70 leading-none mt-0.5">4:5 · IG</span>
        </div>
        {/* Brochure A4 */}
        <div className="w-[54px] h-[78px] rounded-md bg-white border border-[#E5E2DA] shadow-lg flex flex-col justify-end p-2">
          <span className="text-[8px] uppercase font-bold text-[#7C8B6E] tracking-wider leading-none">A4</span>
          <span className="text-[7px] text-[#9A968B] leading-none mt-0.5">PDF stampa</span>
        </div>
      </div>
      <div className="absolute bottom-2 left-0 right-0 text-center text-[9px] uppercase tracking-[0.22em] text-[#7C8B6E] font-semibold">
        Stesso layout · 8 palette · 32 combinazioni
      </div>
    </div>
  );
}

function BeforeAfter() {
  const [pos, setPos] = useState(50);
  // Auto-animate slider on first show
  const animatedRef = React.useRef(false);
  useEffect(() => {
    if (animatedRef.current) return;
    animatedRef.current = true;
    let v = 50; let dir = 1;
    const id = setInterval(() => {
      v += dir * 1.6;
      if (v >= 78) dir = -1;
      if (v <= 22) dir = 1;
      setPos(v);
    }, 32);
    setTimeout(() => clearInterval(id), 2400); // stop after 2.4s
    return () => clearInterval(id);
  }, []);
  return (
    <div
      className="relative h-44 sm:h-52 rounded-xl overflow-hidden bg-[#1C1C1A] select-none cursor-ew-resize"
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setPos(Math.max(2, Math.min(98, ((e.clientX - r.left) / r.width) * 100)));
      }}
      onTouchMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        const t = e.touches[0];
        setPos(Math.max(2, Math.min(98, ((t.clientX - r.left) / r.width) * 100)));
      }}
      data-testid="onboarding-before-after"
    >
      <img src="/landing/camera-after.jpg" alt="Foto migliorata" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src="/landing/camera-before.jpg" alt="Foto originale" className="absolute inset-0 w-full h-full object-cover" />
      </div>
      <div className="absolute top-2 left-2 text-[10px] uppercase tracking-[0.18em] bg-black/60 text-white px-2 py-1 rounded font-bold">Prima</div>
      <div className="absolute top-2 right-2 text-[10px] uppercase tracking-[0.18em] bg-[#7C8B6E] text-white px-2 py-1 rounded font-bold">Dopo</div>
      <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg pointer-events-none" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 -left-3 -translate-y-1/2 w-6 h-6 rounded-full bg-white shadow-md flex items-center justify-center">
          <ArrowLeft size={9} weight="bold" className="text-[#1C1C1A]" />
          <ArrowRight size={9} weight="bold" className="text-[#1C1C1A] -ml-0.5" />
        </div>
      </div>
    </div>
  );
}

function VoiceMock() {
  return (
    <div className="relative h-44 sm:h-52 rounded-xl overflow-hidden bg-white border border-[#EAE7DF] p-4 flex items-center gap-4">
      <div className="shrink-0 w-14 h-14 rounded-full bg-[#1C1C1A] flex items-center justify-center relative">
        <Microphone size={22} weight="fill" className="text-white" />
        <span className="absolute inset-0 rounded-full border-2 border-[#7C8B6E]/60 animate-ping" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#9A968B] font-bold mb-1">
          Trascrizione live
        </div>
        <div className="text-[13.5px] text-[#1C1C1A] leading-snug font-editorial italic">
          «Ricordami di richiamare la signora Ferrari giovedì alle dieci per il
          trilocale di via Brera…»
        </div>
        <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] bg-[#F1F4ED] text-[#7C8B6E] px-2 py-1 rounded-full font-bold">
          <Check size={11} weight="bold" /> Promemoria creato · giovedì 10:00
        </div>
      </div>
    </div>
  );
}

function StimaMock() {
  return (
    <div className="relative h-44 sm:h-52 rounded-xl overflow-hidden bg-white border border-[#EAE7DF] p-4 flex flex-col">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-lg bg-[#F1F4ED] flex items-center justify-center">
          <Calculator size={13} weight="fill" className="text-[#7C8B6E]" />
        </div>
        <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#9A968B] font-bold">
          Stima OMI · Milano · Brera · 95m² · ottimo
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="font-editorial text-[36px] sm:text-[42px] tracking-tight text-[#1C1C1A] leading-none">
            <span className="text-[#9A968B] text-[20px] sm:text-[22px] mr-1">€</span>
            720k <span className="text-[#9A968B] text-[20px] sm:text-[22px]">–</span> 880k
          </div>
          <div className="mt-1.5 text-[11.5px] text-[#6B6A65]">
            range di mercato · confidenza alta
          </div>
        </div>
      </div>
      <div className="text-[10px] text-[#9A968B] text-center">
        Fonte: OMI 2° semestre 2025 · Agenzia delle Entrate
      </div>
    </div>
  );
}
